import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../contexts/LanguageContext";
import { useUTM } from "../hooks/useUTM";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Pricing tier mini modal — collects name + phone (required) and optional
 * email, posts a lead with source="pricing_tier_cta", then opens WhatsApp
 * pre-filled. A small "Hayır, direkt WhatsApp" link bypasses the form.
 */
export default function PricingCtaModal({ open, onClose, payload }) {
  const { t, lang } = useLanguage();
  const { utm, referrer } = useUTM();
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [loading, setLoading] = useState(false);
  const firstFieldRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm({ name: "", phone: "", email: "" });
      setTimeout(() => firstFieldRef.current?.focus(), 80);
    }
  }, [open]);

  if (!payload) return null;
  const { serviceLabel, serviceSlug, tierLabel, formattedPrice, priceTry } = payload;

  const buildWaMessage = () => {
    const greet = (form.name || "").trim()
      ? `Merhaba, ben ${form.name.trim()}.`
      : "Merhaba.";
    return [
      greet,
      `${serviceLabel} hizmetinin ${tierLabel} paketini (${formattedPrice}/ay) seçtim.`,
      "Detaylı bilgi almak istiyorum.",
    ].join(" ");
  };

  const directWhatsApp = () => {
    const url = `https://wa.me/908508884865?text=${encodeURIComponent(buildWaMessage())}`;
    window.open(url, "_blank", "noopener");
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.phone.trim().length < 7) {
      toast.error(t("pricing_modal.invalid", "Ad ve geçerli bir telefon zorunlu."));
      return;
    }
    setLoading(true);
    try {
      // Fire-and-forget lead — don't block WhatsApp redirect on it
      fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim() || "pricing-cta@yastikmedya.com",
          phone: form.phone.trim(),
          service: serviceSlug,
          message: `[Pricing CTA] ${serviceLabel} • ${tierLabel} (${formattedPrice}/ay, base TRY ${priceTry || "?"})`,
          lang,
          landing_page: window.location.pathname,
          source: "pricing_tier_cta",
          utm, referrer,
        }),
      }).catch(() => {});
      const url = `https://wa.me/908508884865?text=${encodeURIComponent(buildWaMessage())}`;
      window.open(url, "_blank", "noopener");
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-[80] bg-ink/55 backdrop-blur-sm flex items-end sm:items-center justify-center px-4 sm:p-6"
          onClick={onClose}
          data-testid="pricing-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.97 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-3xl bg-cream shadow-[0_36px_80px_-30px_rgba(238,79,122,0.55)] border border-mist overflow-hidden"
          >
            <div className="absolute -top-20 -end-12 h-44 w-44 rounded-full bg-brand-pink/25 blur-3xl pointer-events-none" />
            <button
              type="button"
              onClick={onClose}
              aria-label={t("nav.close_menu", "Kapat")}
              className="absolute end-3 top-3 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full bg-cream/80 hover:bg-cream text-ink/55 hover:text-ink transition-colors"
              data-testid="pricing-modal-close"
            >
              <X className="h-4 w-4" />
            </button>

            <form onSubmit={submit} className="relative p-6 sm:p-8">
              <p className="eyebrow"><span className="h-1 w-1 rounded-full bg-brand-pink" />{t("pricing_modal.eyebrow", "HIZLI BAĞLANTI")}</p>
              <h3 className="mt-3 font-display text-2xl font-bold text-ink leading-tight">
                {t("pricing_modal.title", "Bilgilerini bırak, dakikalar içinde dönelim.")}
              </h3>
              <p className="mt-2 text-sm text-ink/55 leading-relaxed">
                <span className="font-semibold text-ink">{tierLabel}</span> paketi · {formattedPrice}/ay
              </p>

              <div className="mt-5 space-y-3">
                <div>
                  <label className="label-base">{t("form.name", "Adın")} *</label>
                  <input ref={firstFieldRef} type="text" required value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder={t("form.name_ph", "Adın Soyadın")}
                    className="input-base"
                    data-testid="pricing-modal-name" />
                </div>
                <div>
                  <label className="label-base">{t("form.phone", "Telefon")} *</label>
                  <input type="tel" required value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+90 5xx xxx xx xx"
                    className="input-base"
                    data-testid="pricing-modal-phone" />
                </div>
                <div>
                  <label className="label-base">{t("form.email", "E-posta")} <span className="text-ink/40 normal-case">({t("pricing_modal.optional", "opsiyonel")})</span></label>
                  <input type="email" value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="ad@firma.com"
                    className="input-base"
                    data-testid="pricing-modal-email" />
                </div>
              </div>

              <button type="submit" disabled={loading}
                className="btn-gold w-full mt-6 inline-flex items-center justify-center gap-2"
                data-testid="pricing-modal-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircle className="h-4 w-4" />}
                {t("pricing_modal.submit", "Devam et — WhatsApp aç")}
              </button>

              <button type="button" onClick={directWhatsApp}
                className="mt-3 w-full text-xs text-ink/55 hover:text-ink transition-colors"
                data-testid="pricing-modal-direct">
                {t("pricing_modal.skip", "Hayır, direkt WhatsApp'a git →")}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
